import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, Check } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface CohortRow {
  cohort_month: string;
  buyers: number; new_buyers: number; returning_buyers: number;
  club_gross: number; avg_club: number; trade_attach_pct: number;
  avg_pre_ltv: number; school_first_pct: number; sp30_first_pct: number;
  brand_new_pct: number; repeat_90d_pct: number;
  post90_club: number; post90_other: number; member_30d_pct: number;
}

interface BuyerRow {
  email: string | null;
  anchor_date: string;
  club_amt: number;
  is_first_ever: boolean;
  trade_attach: boolean;
  pre_ltv: number;
  post90_club: number;
  post90_other: number;
  school_first: boolean;
  sp30_first: boolean;
}

const fmt = (v: number) => '$' + Math.round(Math.abs(Number(v) || 0)).toLocaleString();
const monthLabel = (d: string) => new Date(d + 'T00:00:00').toLocaleString('en', { month: 'short', year: '2-digit' });

/** Days since the cohort month ended — used to flag 90d windows still open. */
const monthAge = (d: string) => {
  const end = new Date(d + 'T00:00:00');
  end.setMonth(end.getMonth() + 1);
  return Math.floor((Date.now() - end.getTime()) / 86_400_000);
};

export default function ClubBuyers() {
  const [since, setSince] = useState('2025-01-01');
  const [until, setUntil] = useState('');
  const [rows, setRows] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CohortRow | null>(null);
  const [buyers, setBuyers] = useState<BuyerRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/club-buyers?since=${since}${until ? `&until=${until}` : ''}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setRows(j.data || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [since, until]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row: CohortRow) => {
    setSelected(row); setBuyers([]); setDetailLoading(true);
    try {
      const month = String(row.cohort_month).slice(0, 10);
      const r = await fetch(`${API_BASE}/financial-reports/club-buyers/detail?month=${month}`);
      const j = await r.json();
      if (j.success) setBuyers(j.data || []);
    } catch { /* empty state */ }
    setDetailLoading(false);
  };

  const tot = rows.reduce((a, r) => ({
    buyers: a.buyers + Number(r.buyers),
    newB: a.newB + Number(r.new_buyers),
    gross: a.gross + Number(r.club_gross),
    tradeW: a.tradeW + Number(r.trade_attach_pct) * Number(r.buyers),
    preW: a.preW + Number(r.avg_pre_ltv) * Number(r.buyers),
    schoolW: a.schoolW + Number(r.school_first_pct) * Number(r.buyers),
  }), { buyers: 0, newB: 0, gross: 0, tradeW: 0, preW: 0, schoolW: 0 });
  const w = (v: number) => tot.buyers > 0 ? v / tot.buyers : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Club Buyers</h1>
          <p className="text-sm text-gray-500">
            Custom Club buyers by month — who they are before the purchase (prior spend, school/SP30 funnel), trade-in attach, and what they buy in the 90 days after.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={e => setSince(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          ['Buyer-months', tot.buyers.toLocaleString()],
          ['First-ever buyers', tot.newB.toLocaleString()],
          ['Club gross', fmt(tot.gross)],
          ['Trade-in attach', w(tot.tradeW).toFixed(1) + '%'],
          ['Avg prior spend', fmt(w(tot.preW))],
          ['School first', w(tot.schoolW).toFixed(1) + '%'],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Month', 'Buyers', 'New / Return', 'Club gross', 'Avg buyer', 'Trade attach', 'Prior spend', 'School first', 'SP30 first', 'Brand new', 'Repeat 90d', '+90d clubs', '+90d other'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => {
              const age = monthAge(String(r.cohort_month).slice(0, 10));
              return (
                <tr key={r.cohort_month} onClick={() => openDetail(r)} className="cursor-pointer hover:bg-blue-50">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    {monthLabel(String(r.cohort_month).slice(0, 10))}
                    {age < 90 && <span className="ml-1.5 text-[10px] uppercase text-amber-600 bg-amber-50 rounded px-1 py-0.5">window open</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{r.buyers}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{r.new_buyers} / {r.returning_buyers}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmt(r.club_gross)}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(r.avg_club)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-cyan-700">{r.trade_attach_pct}%</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(r.avg_pre_ltv)}</td>
                  <td className="px-3 py-2 text-right">{r.school_first_pct}%</td>
                  <td className="px-3 py-2 text-right text-gray-500">{r.sp30_first_pct}%</td>
                  <td className="px-3 py-2 text-right text-gray-500">{r.brand_new_pct}%</td>
                  <td className="px-3 py-2 text-right">{r.repeat_90d_pct}%</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(r.post90_club)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.post90_other)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{monthLabel(String(selected.cohort_month).slice(0, 10))} — {selected.buyers} club buyers</h2>
                <p className="text-sm text-gray-500">Sorted by club spend; upstream funnel and 90-day follow-on per buyer</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {detailLoading ? (
                <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
              ) : buyers.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No buyers found.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b">
                      <th className="text-left py-2 pr-2">Buyer</th>
                      <th className="text-right py-2 px-2">Clubs</th>
                      <th className="text-center py-2 px-2">First ever</th>
                      <th className="text-center py-2 px-2">Trade</th>
                      <th className="text-right py-2 px-2">Prior spend</th>
                      <th className="text-center py-2 px-2">School first</th>
                      <th className="text-right py-2 px-2">+90d clubs</th>
                      <th className="text-right py-2 pl-2">+90d other</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyers.map((b, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-1.5 pr-2 truncate max-w-[220px]">{b.email || <span className="text-gray-400">(no email on record)</span>}</td>
                        <td className="text-right py-1.5 px-2 font-medium">{fmt(b.club_amt)}</td>
                        <td className="text-center py-1.5 px-2">{b.is_first_ever ? <Check className="h-4 w-4 text-emerald-600 inline" /> : <span className="text-gray-300">--</span>}</td>
                        <td className="text-center py-1.5 px-2">{b.trade_attach ? <Check className="h-4 w-4 text-cyan-600 inline" /> : <span className="text-gray-300">--</span>}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{fmt(b.pre_ltv)}</td>
                        <td className="text-center py-1.5 px-2">{b.school_first ? <Check className="h-4 w-4 text-amber-600 inline" /> : <span className="text-gray-300">--</span>}</td>
                        <td className="text-right py-1.5 px-2">{fmt(b.post90_club)}</td>
                        <td className="text-right py-1.5 pl-2">{fmt(b.post90_other)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        A buyer appears in every month they bought clubs (buyer-month episodes), anchored to that month's first club order — so range totals can count a
        repeat buyer more than once; "New / Return" splits first-ever club buyers from returning ones. +90d clubs includes trade-in escrow lines (volume,
        not net revenue). Months tagged "window open" haven't finished their 90-day window. Click a month for the buyer list. Data refreshes hourly.
      </p>
    </div>
  );
}
